/**
 * Before/After Image Comparison Slider — barrierefrei, ohne Dependencies.
 *
 * Single Source of Truth: das native <input type="range">. JS hört auf
 * dessen `input`-Event, synchronisiert die CSS-Variable `--pos` (0–100) und
 * aktualisiert `aria-valuetext` in Klartext. Tastatur-Steuerung (Pfeile,
 * Page Up/Down, Home/End) kommt damit gratis vom Browser.
 *
 * Auto-Init aller `[data-before-after]` Elemente bei DOMContentLoaded plus
 * MutationObserver für Ajax-nachgeladene Slider (Contao Frontend-Module).
 */

(function () {
  "use strict";

  const READY_ATTR = "data-ba-ready";
  const REDUCED = window.matchMedia("(prefers-reduced-motion: reduce)");

  function init(figure) {
    if (figure.hasAttribute(READY_ATTR)) return;
    const range = figure.querySelector("[data-ba-range]");
    const stage = figure.querySelector("[data-ba-stage]");
    if (!range || !stage) return;

    figure.setAttribute(READY_ATTR, "true");

    const setPos = (value, animate) => {
      const v = Math.max(0, Math.min(100, Math.round(value)));
      if (animate && !REDUCED.matches) {
        figure.classList.add("is-animating");
        // CSS transition läuft; nach 300ms wieder freigeben für Drag.
        window.setTimeout(() => figure.classList.remove("is-animating"), 320);
      }
      figure.style.setProperty("--pos", String(v));
      range.value = String(v);
      range.setAttribute(
        "aria-valuetext",
        v + " Prozent Vorher-Aufnahme sichtbar",
      );
    };

    // Range-Event → Position
    range.addEventListener("input", () => setPos(range.value, false));

    // Pointer-Events auf der Stage: Drag (Maus + Touch + Stift vereinheitlicht).
    let dragging = false;
    const positionFromEvent = (event) => {
      const rect = stage.getBoundingClientRect();
      if (rect.width <= 0) return parseFloat(range.value) || 0;
      const x = event.clientX - rect.left;
      return (x / rect.width) * 100;
    };

    stage.addEventListener("pointerdown", (event) => {
      // Klicks auf Range, Captions, etc. nicht doppelt verarbeiten.
      if (event.target.closest("[data-ba-range]")) return;
      const isOnHandle = !!event.target.closest(".ds-ba__handle, .ds-ba__divider");
      const next = positionFromEvent(event);

      if (isOnHandle) {
        // Direkt mit dem Drag starten — kein Animation-Hop unter dem Finger.
        dragging = true;
        stage.setPointerCapture(event.pointerId);
        setPos(next, false);
      } else {
        // Klick irgendwo auf die Stage → animiert dorthin springen (außer bei reduced-motion).
        setPos(next, true);
      }
    });

    stage.addEventListener("pointermove", (event) => {
      if (!dragging) return;
      setPos(positionFromEvent(event), false);
    });

    const endDrag = (event) => {
      if (!dragging) return;
      dragging = false;
      try {
        stage.releasePointerCapture(event.pointerId);
      } catch (e) {
        /* pointer kann verloren gehen — silently ignorieren */
      }
    };
    stage.addEventListener("pointerup", endDrag);
    stage.addEventListener("pointercancel", endDrag);

    // Initial-Wert aus data-initial (oder Range default) anwenden.
    const initial = parseInt(figure.dataset.initial || range.value || "50", 10);
    setPos(initial, false);
  }

  function initAll(root) {
    const scope = root && root.querySelectorAll ? root : document;
    scope.querySelectorAll("[data-before-after]").forEach(init);
  }

  function observe() {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((m) => {
        m.addedNodes.forEach((node) => {
          if (node.nodeType !== 1) return;
          if (node.matches && node.matches("[data-before-after]")) init(node);
          if (node.querySelectorAll) initAll(node);
        });
      });
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => {
      initAll();
      observe();
    });
  } else {
    initAll();
    observe();
  }
})();
